import { useMemo } from 'react';
import '@/core/styles/globals.css';
import { Router } from '@/app/router/Router';
import { ReduxProvider } from '@/app/providers/ReduxProvider';
import { createPreloadedState, createStore } from '@/app/store/store';
import type { MenuApiResponse } from '@/shared/features/menu/infra/adapters/menuApiSlice';
import { ToastProvider } from '@/shared/widgets/toast';
import { FeatureFlagsPrefetch } from '@/features/feature-flags/presentation/components/FeatureFlagsPrefetch';
import { FreshchatWidget } from '@/shared/widgets/FreshchatWidget';
import { PushNotificationWidget } from '@/shared/widgets/PushNotificationWidget';
import type { RouterProviderProps } from 'react-router-dom';
import type { ListingPageResult } from '@/features/plp/domain/models/ProductListing';
import { PosApprovalModel } from '@/features/pos-approval/domain/models/PosApprovalModel';
import type { ProductDetail } from '@/features/pdp/domain/models/ProductDetail';
import type { PhysicalGiftCardConfig } from '@/features/store-gc/domain/models/storeGiftCard.types';
import type { PhysicalStoresDetails } from '@/shared/features/physical-store/domain/models/PhysicalStoresDetails';
import type { StoreLocatorData } from '@/shared/features/physical-store/domain/models/StoreLocatorData';

type StoreType = ReturnType<typeof createStore>;

// Get initial data from window (injected by SSR)
declare global {
  interface Window {
    __INITIAL_MENU_RESULT__?: MenuApiResponse;
    __INITIAL_HOME_PAGE_DATA__?: Record<string, unknown>;
    __INITIAL_USER_CONTEXT__?: Record<string, unknown> | null;
    __INITIAL_PLP_DATA__?: ListingPageResult | null;
    /** Pathname+search the PLP payload was fetched for (pairs with __INITIAL_PLP_DATA__). */
    __INITIAL_PLP_URL__?: string | null;
    __INITIAL_POS_APPROVAL_DATA__?: PosApprovalModel | null;
    __INITIAL_PDP_DATA__?: ProductDetail | null;
    __INITIAL_STORE_GIFT_CARD_CONFIG__?: PhysicalGiftCardConfig | null;
    /** Store/city top banner data for `/stores/*catalog.html` routes (pairs with `physicalStoreApi`'s cache). */
    __INITIAL_PHYSICAL_STORES_DETAILS__?: PhysicalStoresDetails | null;
    /** Store locator page data for `/store.html` (pairs with `getStoreLocator` cache). */
    __INITIAL_STORE_LOCATOR_DATA__?: StoreLocatorData | null;
  }
}

const initialMenuResult = typeof window !== 'undefined' ? window.__INITIAL_MENU_RESULT__ : null;
const initialHomePageData = typeof window !== 'undefined' ? window.__INITIAL_HOME_PAGE_DATA__ : null;
const initialUserContextData = typeof window !== 'undefined' ? window.__INITIAL_USER_CONTEXT__ : null;
const initialPlpData = typeof window !== 'undefined' ? window.__INITIAL_PLP_DATA__ : null;
const initialPlpUrlFromWindow =
  typeof window !== 'undefined' ? (window.__INITIAL_PLP_URL__ ?? null) : null;
const initialPosApprovalDataFromWindow =
  typeof window !== 'undefined' ? window.__INITIAL_POS_APPROVAL_DATA__ : null;
const initialPdpData = typeof window !== 'undefined' ? window.__INITIAL_PDP_DATA__ : null;
const initialStoreGiftCardConfigFromWindow =
  typeof window !== 'undefined' ? (window.__INITIAL_STORE_GIFT_CARD_CONFIG__ ?? null) : null;
const initialPhysicalStoresDetailsFromWindow =
  typeof window !== 'undefined' ? (window.__INITIAL_PHYSICAL_STORES_DETAILS__ ?? null) : null;
const initialStoreLocatorDataFromWindow =
  typeof window !== 'undefined' ? (window.__INITIAL_STORE_LOCATOR_DATA__ ?? null) : null;

export default function App(props?: {
  /**
   * SSR only: use this store so the render tree sees the same state that was filled by executeQuery (menu + home).
   * On the client this is never passed.
   */
  store?: StoreType;
  /**
   * SSR override: provide a memory router so SSR and hydration share the same component tree.
   */
  router?: RouterProviderProps['router'];
  /**
   * SSR override: prefer passing these from `server/entry-server.tsx`.
   * On the client, these are read from `window.__INITIAL_*__`.
   */
  initialMenuResult?: MenuApiResponse | null;
  initialHomePageData?: Record<string, unknown> | null;
  initialUserContextData?: Record<string, unknown> | null;
  initialPlpData?: ListingPageResult | null;
  /** SSR: pathname+search the PLP payload was fetched for (pairs with initialPlpData). */
  initialPlpUrl?: string | null;
  initialAbUid?: string | null;
  initialPosApprovalData?: PosApprovalModel | null;
  initialPdpData?: ProductDetail | null;
  initialStoreGiftCardConfig?: PhysicalGiftCardConfig | null;
  initialPhysicalStoresDetails?: PhysicalStoresDetails | null;
  initialStoreLocatorData?: StoreLocatorData | null;
}) {
  const menuResult = props?.initialMenuResult ?? initialMenuResult;
  const homePageData = props?.initialHomePageData ?? initialHomePageData;
  const userContextData = props?.initialUserContextData ?? initialUserContextData;
  const plpData = props?.initialPlpData ?? initialPlpData ?? null;
  const plpUrl = props?.initialPlpUrl ?? initialPlpUrlFromWindow ?? null;
  const posApprovalData = props?.initialPosApprovalData ?? initialPosApprovalDataFromWindow ?? null;
  const pdpData = props?.initialPdpData ?? initialPdpData ?? null;
  const storeGiftCardConfig =
    props?.initialStoreGiftCardConfig ?? initialStoreGiftCardConfigFromWindow ?? null;
  const physicalStoresDetails =
    props?.initialPhysicalStoresDetails ?? initialPhysicalStoresDetailsFromWindow ?? null;
  const storeLocatorData =
    props?.initialStoreLocatorData ?? initialStoreLocatorDataFromWindow ?? null;

  // Bucketing key preference:
  // - SSR: `props.initialAbUid` is provided by `server/entry-server.tsx`
  // - Client: derive from user-context visitorId, else fallback to existing cookie
  const abUidFromUserContext = (() => {
    const raw = (userContextData as Record<string, unknown> | null | undefined)?.visitorId;
    return typeof raw === 'number' && Number.isFinite(raw) ? String(raw) : null;
  })();
  const abUid = props?.initialAbUid ?? abUidFromUserContext;

  // Preload state is only used for abTesting + userContext shape; menu/home are seeded in ReduxProvider.
  // Memoize so ReduxProvider's useMemo doesn't see a new reference every render and recreate the store.
  const preloadedState = useMemo(
    () =>
      createPreloadedState(
        menuResult || null,
        homePageData || null,
        // Do NOT preload user-context into RTK Query state (crashes hydration). useUserContext() reads window.
        null,
        abUid || null,
        plpData ?? null,
        plpUrl,
        posApprovalData,
        pdpData ?? null,
        storeGiftCardConfig,
        storeLocatorData
      ),
    [menuResult, homePageData, abUid, plpData, plpUrl, posApprovalData, pdpData, storeGiftCardConfig, storeLocatorData]
  );
  
  return (
    <ReduxProvider
      store={props?.store}
      preloadedState={preloadedState}
      initialMenuResult={menuResult ?? null}
      initialHomePageData={homePageData}
      initialPhysicalStoresDetails={physicalStoresDetails}
      initialStoreLocatorData={storeLocatorData}
    >
      <FeatureFlagsPrefetch />
      <ToastProvider>
        <Router router={props?.router} />
        <FreshchatWidget />
        <PushNotificationWidget />
      </ToastProvider>
    </ReduxProvider>
  );
}
